import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react'
import { ChevronLeft, ChevronRight, Play, Pause, SkipForward, SkipBack, Clock, Zap, ChevronDown, Pin, PinOff, TrendingUp, TrendingDown, RotateCcw } from 'lucide-react'
import { useSettingsStore } from '../../stores/settingsStore'
import { usePlaybackStore } from '../../stores/playbackStore'

export interface TimeRangeSlicerProps
{
    minMinutes?: number
    maxMinutes?: number
    onChange: ( startStr: string, endStr: string ) => void
    viewMode: 'snapshot' | 'comparison'
    onViewModeChange: ( mode: 'snapshot' | 'comparison' ) => void
    availableTimes?: string[] // HH:mm format from intraday history
    className?: string
    hideModeToggle?: boolean
    intervalMinutes?: number
    onIntervalChange?: (interval: number) => void
    /** When true, always initialise to full-day (maxMinutes) and ignore localStorage */
    forceFullDay?: boolean
    /** Index LTP data for the chip display */
    indexLtp?: number
    dayChange?: number
    dayChangeP?: number
    symbol?: string
    /** Callback to reset slicer drag position */
    onReset?: () => void
}

const PRESETS = [
    { label: '5M', min: 5 },
    { label: '15M', min: 15 },
    { label: '30M', min: 30 },
    { label: 'Day', min: null },
]

const SPEEDS = [ 1, 2, 5, 10, 20 ] as const

const formatMinutes = ( m: number ) =>
{
    const hh = Math.floor( m / 60 )
    const mm = m % 60
    const ampm = hh >= 12 ? 'PM' : 'AM'
    let dispH = hh % 12
    if ( dispH === 0 ) dispH = 12
    return `${dispH}:${mm.toString().padStart( 2, '0' )} ${ampm}`
}

const toTimeString = ( m: number ) =>
{
    const hh = Math.floor( m / 60 )
    const mm = m % 60
    return `${hh.toString().padStart( 2, '0' )}:${mm.toString().padStart( 2, '0' )}`
}

const timeStrToMin = ( t: string ) =>
{
    const [ h, m ] = t.split( ':' ).map( Number )
    return ( h || 0 ) * 60 + ( m || 0 )
}

// PERF FIX: Extract static CSS to a module-level constant.
// Previously this was inside dangerouslySetInnerHTML which recreated the object
// and forced DOM style injection on every render cycle (every 80ms during playback).
const SLIDER_STYLES = `
    input[type=range]::-webkit-slider-thumb {
        pointer-events: auto;
        appearance: none;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: white;
        border: 1px solid #e2e8f0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px rgba(255,255,255,0.6);
        cursor: grab;
        margin-top: -8px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    input[type=range]::-webkit-slider-thumb:hover {
        transform: scale(1.15);
        box-shadow: 0 6px 16px rgba(0,0,0,0.2), 0 0 0 6px rgba(255,255,255,0.9);
    }
    input[type=range]::-webkit-slider-thumb:active {
        cursor: grabbing;
        transform: scale(0.95);
    }
    input[type=range]::-moz-range-thumb {
        pointer-events: auto;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: white;
        border: 1px solid #e2e8f0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px rgba(255,255,255,0.6);
        cursor: grab;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    input[type=range]::-moz-range-thumb:hover {
        transform: scale(1.15);
        box-shadow: 0 6px 16px rgba(0,0,0,0.2), 0 0 0 6px rgba(255,255,255,0.9);
    }
`

// PERF FIX: Stable style object reference — never recreated
const SLIDER_STYLE_HTML = { __html: SLIDER_STYLES }

export const TimeRangeSlicer: React.FC<TimeRangeSlicerProps> = React.memo(( {
    minMinutes = 555,
    maxMinutes = 930, // 15:30 market close
    onChange,
    viewMode,
    onViewModeChange,
    availableTimes = [],
    className,
    hideModeToggle = false,
    intervalMinutes = 1,
    onIntervalChange,
    forceFullDay = false,
    indexLtp = 0,
    dayChange = 0,
    dayChangeP = 0,
    symbol = '',
    onReset,
} ) =>
{
    const [ startMin, setStartMin ] = useState( () => minMinutes )
    const [ endMin, setEndMin ] = useState( () => maxMinutes )
    const [ isPending, startTransition ] = useTransition()
    const snapshotMin = usePlaybackStore(state => state.snapshotMin)
    const setSnapshotMin = usePlaybackStore(state => state.setSnapshotMin)
    const isSlicerSticky = useSettingsStore( ( s ) => s.isSlicerSticky )
    const setIsSlicerSticky = useSettingsStore( ( s ) => s.setIsSlicerSticky )
    const [ activePreset, setActivePreset ] = useState<string | null>( () => 'Day' )

    // When forceFullDay changes to true (e.g. user switches to Historical mode),
    // reset the slicer to full day
    useEffect( () =>
    {
        if ( forceFullDay )
        {
            setStartMin( minMinutes )
            setEndMin( maxMinutes )
            setSnapshotMin( maxMinutes )
            setActivePreset( 'Day' )
            usePlaybackStore.getState().setPlaying( false )
        }
    }, [ forceFullDay, minMinutes, maxMinutes ] )
    const playSpeed = usePlaybackStore(state => state.playSpeed)
    const setPlaySpeed = usePlaybackStore(state => state.setPlaySpeed)
    const isPlaying = usePlaybackStore(state => state.isPlaying)
    const setIsPlaying = usePlaybackStore(state => state.setPlaying)
    const togglePlayback = usePlaybackStore(state => state.togglePlayback)
    const setGlobalAvailableMinutes = usePlaybackStore(state => state.setAvailableMinutes)

    const playRef = useRef<ReturnType<typeof setInterval> | null>( null )

    // Available time indices for step-through
    const availableMinutes = useMemo( () =>
    {
        if ( !availableTimes?.length ) return []
        const mins = availableTimes.map( t =>
        {
            const [ h, m ] = t.split( ':' ).map( Number )
            return ( h || 0 ) * 60 + ( m || 0 )
        } ).filter( ( m ) => m >= minMinutes && m <= maxMinutes ).sort( ( a, b ) => a - b )
        
        return Array.from(new Set(mins));
    }, [ availableTimes, minMinutes, maxMinutes ] )

    useEffect(() => {
        setGlobalAvailableMinutes(availableMinutes)
    }, [availableMinutes, setGlobalAvailableMinutes])

    // Snap slider value to nearest available time
    const snapToNearest = useCallback( ( val: number ) =>
    {
        if ( availableMinutes.length === 0 ) return val
        let bestIdx = 0
        let bestDiff = Math.abs( availableMinutes[ 0 ]! - val )
        for ( let i = 1; i < availableMinutes.length; i++ )
        {
            const diff = Math.abs( availableMinutes[ i ]! - val )
            if ( diff < bestDiff ) { bestDiff = diff; bestIdx = i }
        }
        return availableMinutes[ bestIdx ]!
    }, [ availableMinutes ] )

    // PERF FIX: We MUST use Concurrent React correctly here.
    // By wrapping the onChange in startTransition, React knows it's a background update.
    // By checking !isPending, we GUARANTEE we never fire a new update while the previous one 
    // is still rendering! This prevents React from aborting the heavy table renders.
    const lastDispatchedRef = useRef<string>('')
    const throttleStateRef = useRef<{ isThrottled: boolean, pendingArgs: [string, string] | null }>({ isThrottled: false, pendingArgs: null })

    useEffect( () =>
    {
        if ( isPending ) return; // DO NOT abort the ongoing background render!
        
        let newStart = '', newEnd = '';
        if ( viewMode === 'snapshot' )
        {
            newStart = toTimeString( snapshotMin );
            newEnd = toTimeString( snapshotMin );
        } else
        {
            newStart = toTimeString( startMin );
            newEnd = toTimeString( endMin );
        }

        const dispatchKey = `${newStart}-${newEnd}`;
        if (lastDispatchedRef.current === dispatchKey) return;

        if (throttleStateRef.current.isThrottled) {
            throttleStateRef.current.pendingArgs = [newStart, newEnd];
            return;
        }

        throttleStateRef.current.isThrottled = true;
        lastDispatchedRef.current = dispatchKey;
        
        startTransition(() => {
            onChange( newStart, newEnd )
        })

        setTimeout(() => {
            throttleStateRef.current.isThrottled = false;
            const pending = throttleStateRef.current.pendingArgs;
            if (pending) {
                throttleStateRef.current.pendingArgs = null;
                const newKey = `${pending[0]}-${pending[1]}`;
                if (lastDispatchedRef.current !== newKey) {
                    lastDispatchedRef.current = newKey;
                    startTransition(() => {
                        onChange(pending[0], pending[1]);
                    });
                }
            }
        }, 150); // 150ms throttle (~6fps) allows the massive MarketWatchSection to actually finish rendering without freezing the browser
    }, [ startMin, endMin, snapshotMin, viewMode, onChange, isPending ] )

    const stepForward = usePlaybackStore(s => s.stepForward)
    const stepBackward = usePlaybackStore(s => s.stepBackward)

    const handlePreset = useCallback( ( label: string, minsBack: number | null ) =>
    {
        setActivePreset( label )
        setIsPlaying( false )
        if ( viewMode === 'comparison' )
        {
            if ( minsBack === null )
            {
                setStartMin( minMinutes )
                setEndMin( maxMinutes )
            } else
            {
                setEndMin( maxMinutes )
                setStartMin( Math.max( minMinutes, maxMinutes - minsBack ) )
            }
        } else
        {
            // In snapshot mode, presets jump to the time
            if ( minsBack === null )
            {
                setSnapshotMin( maxMinutes )
            } else
            {
                setSnapshotMin( Math.max( minMinutes, maxMinutes - minsBack ) )
            }
        }
    }, [viewMode, minMinutes, maxMinutes, setIsPlaying])

    const startPct = ( ( startMin - minMinutes ) / ( maxMinutes - minMinutes ) ) * 100
    const endPct = ( ( endMin - minMinutes ) / ( maxMinutes - minMinutes ) ) * 100
    const snapPct = ( ( snapshotMin - minMinutes ) / ( maxMinutes - minMinutes ) ) * 100

    // PERF FIX: Limit the number of time dots rendered.
    // With 375 1-minute snapshots, rendering 375 absolutely-positioned divs
    // causes layout thrashing during slider drag. Downsample to max 60 dots.
    const downsampledDots = useMemo( () =>
    {
        if ( availableMinutes.length <= 60 ) return availableMinutes
        const step = Math.ceil( availableMinutes.length / 60 )
        return availableMinutes.filter( ( _, i ) => i % step === 0 )
    }, [ availableMinutes ] )

    const jumpTime = usePlaybackStore(s => s.jumpTime)

    return (
        <div className={ `w-full flex flex-col gap-2 p-1.5 px-2 bg-white/80 dark:bg-slate-900/80 backdrop-blur-3xl rounded-[16px] sm:rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm ${className || ''}` }>

            {/* All Controls (Wraps on small screens) */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 w-full shrink-0">

                {/* Index LTP Chip */}
                {indexLtp > 0 && (
                    <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 px-2.5 py-1 rounded-full border border-slate-200/50 dark:border-slate-700/30 shrink-0">
                        <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">{symbol || 'INDEX'}</span>
                        <span className="text-[12px] font-black tabular-nums text-slate-800 dark:text-slate-100">
                            {indexLtp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-black tabular-nums ${
                            dayChange >= 0
                                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                        }`}>
                            {dayChange >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                            <span>{dayChange >= 0 ? '+' : ''}{dayChange.toFixed(2)}</span>
                            <span className="opacity-70">({dayChangeP >= 0 ? '+' : ''}{dayChangeP.toFixed(2)}%)</span>
                        </div>
                    </div>
                )}
                
                {/* Mode Toggle */}
                {!hideModeToggle && (
                    <div className="flex bg-slate-100/80 dark:bg-slate-800/80 p-0.5 rounded-full border border-slate-200/50 dark:border-slate-700/30 shrink-0">
                        <button
                            onClick={() => { onViewModeChange('snapshot'); setIsPlaying(false) }}
                            className={`px-3 py-1 flex items-center gap-1.5 rounded-full text-[10px] font-black transition-all duration-300 ${viewMode === 'snapshot' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}
                        >
                            <Clock size={12} className={viewMode === 'snapshot' ? 'opacity-100' : 'opacity-50'} />
                            History
                        </button>
                        <button
                            onClick={() => { onViewModeChange('comparison'); setIsPlaying(false) }}
                            className={`px-3 py-1 flex items-center gap-1.5 rounded-full text-[10px] font-black transition-all duration-300 ${viewMode === 'comparison' ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}
                        >
                            <Zap size={12} className={viewMode === 'comparison' ? 'opacity-100' : 'opacity-50'} />
                            Live
                        </button>
                        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 my-auto mx-1" />
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsSlicerSticky(!isSlicerSticky); }}
                            className={`px-2 py-1 flex items-center gap-1.5 rounded-full text-[10px] font-black transition-all duration-300 ${isSlicerSticky ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}
                            title={isSlicerSticky ? "Unpin Slicer" : "Pin Slicer to Top"}
                        >
                            {isSlicerSticky ? <Pin size={12} className="opacity-100" /> : <PinOff size={12} className="opacity-50" />}
                            Pin
                        </button>
                        {onReset && (
                            <>
                                <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 my-auto mx-1" />
                                <button
                                    onClick={(e) => { e.stopPropagation(); onReset(); }}
                                    className="px-2 py-1 flex items-center gap-1.5 rounded-full text-[10px] font-black transition-all duration-300 text-slate-500 hover:text-slate-800 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700"
                                    title="Reset slicer position"
                                >
                                    <RotateCcw size={12} className="opacity-50" />
                                    Reset
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Playback Controls (Snapshot) */}
                {viewMode === 'snapshot' && (
                    <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 p-1 rounded-lg sm:rounded-full border border-slate-200/50 dark:border-slate-700/30 shrink-0">
                        {/* Jump Left */}
                        <div className="flex items-center gap-1 pr-1 sm:pr-2 border-r border-slate-200 dark:border-slate-700">
                            <button onClick={() => { setSnapshotMin(availableMinutes[0] || minMinutes); setActivePreset(null) }} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">Start</button>
                            <button onClick={() => jumpTime(-15)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&lt;&lt;15m</button>
                            <button onClick={() => jumpTime(-10)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&lt;&lt;10m</button>
                            <button onClick={() => jumpTime(-5)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&lt;&lt;5m</button>
                            <button onClick={() => jumpTime(-3)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&lt;&lt;3m</button>
                            <button onClick={() => jumpTime(-1)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&lt;&lt;1m</button>
                        </div>

                        {/* Play / Step */}
                        <div className="flex items-center px-1 gap-1">
                            <button onClick={() => togglePlayback()} className={`py-1 px-4 sm:px-5 rounded bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm transition-all ${isPlaying ? 'bg-rose-500 hover:bg-rose-600' : ''}`}>
                                {isPlaying ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
                            </button>
                        </div>

                        {/* Jump Right */}
                        <div className="flex items-center gap-1 pl-1 sm:pl-2 border-l border-slate-200 dark:border-slate-700">
                            <button onClick={() => jumpTime(1)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&gt;&gt;1m</button>
                            <button onClick={() => jumpTime(3)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&gt;&gt;3m</button>
                            <button onClick={() => jumpTime(5)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&gt;&gt;5m</button>
                            <button onClick={() => jumpTime(10)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&gt;&gt;10m</button>
                            <button onClick={() => jumpTime(15)} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">&gt;&gt;15m</button>
                            <button onClick={() => { setSnapshotMin(availableMinutes[availableMinutes.length - 1] || maxMinutes); setActivePreset(null) }} className="px-2 py-1 rounded bg-slate-200/60 hover:bg-slate-300 dark:bg-slate-700/60 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-black transition-colors">End</button>
                        </div>
                        
                        {/* Speed Dropdown */}
                        <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 hidden lg:block mx-1"></div>
                        <div className="relative hidden lg:flex items-center">
                            <select
                                value={playSpeed}
                                onChange={(e) => setPlaySpeed(Number(e.target.value) as typeof playSpeed)}
                                className="appearance-none bg-transparent outline-none pl-1 pr-5 py-0.5 text-[10px] font-black cursor-pointer text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors z-10 relative"
                            >
                                {SPEEDS.map(speed => (
                                    <option key={speed} value={speed} className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">{speed}x</option>
                                ))}
                            </select>
                            <ChevronDown size={10} className="absolute right-1 pointer-events-none text-slate-400" />
                        </div>
                    </div>
                )}

                {/* Presets Dropdown */}
                <div className="relative flex items-center shrink-0">
                    <select
                        value={activePreset || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const preset = PRESETS.find(p => p.label === val);
                            if (preset) handlePreset(preset.label, preset.min);
                        }}
                        className="appearance-none bg-slate-100/80 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 text-[10px] sm:text-[11px] font-black rounded-full border border-slate-200/50 dark:border-slate-700/50 pl-3 pr-7 py-1 outline-none cursor-pointer hover:bg-white dark:hover:bg-slate-800 transition-colors z-10 relative h-[28px]"
                    >
                        <option value="" disabled className="text-slate-400 bg-white dark:bg-slate-800">Select...</option>
                        {PRESETS.map((preset) => (
                            <option key={preset.label} value={preset.label} className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">{preset.label}</option>
                        ))}
                    </select>
                    <ChevronDown size={12} className="absolute right-2 pointer-events-none text-slate-400" />
                </div>
                
                {/* Current Time Display / Selector */}
                <div className="px-2 shrink-0 bg-slate-100/80 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 text-[10px] sm:text-[11px] font-black rounded-full border border-slate-200/50 dark:border-slate-700/50 tracking-wide flex items-center justify-center min-w-[90px] hover:bg-white dark:hover:bg-slate-800 transition-colors h-[28px]">
                    {viewMode === 'snapshot' ? (
                        <div className="relative flex items-center w-full">
                            <select 
                                value={snapshotMin} 
                                onChange={(e) => {
                                    setSnapshotMin(Number(e.target.value));
                                    setActivePreset(null);
                                    setIsPlaying(false);
                                }}
                                className="appearance-none bg-transparent outline-none w-full text-center pr-5 font-black cursor-pointer text-slate-800 dark:text-slate-100"
                            >
                                {availableMinutes.length > 0 ? (
                                    [...availableMinutes].reverse().map(min => (
                                        <option key={min} value={min} className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">{formatMinutes(min)}</option>
                                    ))
                                ) : (
                                    <option value={snapshotMin} className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">{formatMinutes(snapshotMin)}</option>
                                )}
                            </select>
                            <ChevronDown size={14} className="absolute right-0 pointer-events-none text-slate-400" />
                        </div>
                    ) : (
                        `${formatMinutes(startMin)} → ${formatMinutes(endMin)}`
                    )}
                </div>
            </div>

            {/* Bottom Row: Premium Slider */ }
            <div className="flex items-center gap-3 px-1 sm:px-2 flex-1 min-w-[200px] w-full text-[10px] sm:text-[11px] font-bold text-slate-400 tracking-wide uppercase">
                <span className="w-12 text-right whitespace-nowrap">{ formatMinutes( minMinutes ) }</span>

                <div className="flex-1 relative h-6 flex items-center group/slider">
                    {/* Track Background */ }
                    <div className="absolute left-0 right-0 h-[6px] bg-slate-100 dark:bg-slate-700/80 rounded-full overflow-hidden shadow-[inset_0_1px_4px_rgba(0,0,0,0.06)]" />

                    { viewMode === 'comparison' ? (
                        <>
                            {/* Active Comparison Track */ }
                            <div
                                className="absolute h-[6px] bg-linear-to-r from-emerald-400 to-teal-500 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                                style={ { left: startPct + '%', right: 100 - endPct + '%' } }
                            />

                            <input type="range" min={ minMinutes } max={ maxMinutes } value={ startMin } onChange={ ( e ) => { const v = parseInt( e.target.value ); if ( v <= endMin ) { setStartMin( v ); setActivePreset( null ); } } } className="absolute w-full appearance-none pointer-events-none bg-transparent h-6" style={ { zIndex: 3 } } />
                            <input type="range" min={ minMinutes } max={ maxMinutes } value={ endMin } onChange={ ( e ) => { const v = parseInt( e.target.value ); if ( v >= startMin ) { setEndMin( v ); setActivePreset( null ); } } } className="absolute w-full appearance-none pointer-events-none bg-transparent h-6" style={ { zIndex: 4 } } />
                        </>
                    ) : (
                        <>
                            {/* Snapshot Track Fill */ }
                            <div
                                className="absolute h-[6px] bg-linear-to-r from-indigo-400 to-violet-500 rounded-full shadow-[0_0_12px_rgba(99,102,241,0.3)] transition-all duration-150"
                                style={ { left: 0, right: 100 - snapPct + '%' } }
                            />

                            {/* Snapshot marker (Thumb overlay) */ }
                            <div
                                className="absolute h-4 w-4 bg-white border-[3px] border-violet-500 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.15)] transition-all duration-150 pointer-events-none z-10"
                                style={ { left: snapPct + '%', transform: 'translateX(-50%)' } }
                            />

                            {/* Available time dots — PERF FIX: Downsampled to max 60 */ }
                            { downsampledDots.map( ( m ) =>
                            {
                                const pct = ( ( m - minMinutes ) / ( maxMinutes - minMinutes ) ) * 100
                                return (
                                    <div key={ m } className="absolute w-1 h-1 rounded-full bg-white/60 dark:bg-slate-400 mix-blend-overlay" style={ { left: pct + '%', top: '50%', transform: 'translate(-50%, -50%)' } } />
                                )
                            } ) }

                            <input type="range" min={ minMinutes } max={ maxMinutes } value={ snapshotMin } onChange={ ( e ) => { setSnapshotMin( snapToNearest( parseInt( e.target.value ) ) ); setActivePreset( null ); setIsPlaying( false ); } } className="absolute w-full appearance-none pointer-events-auto bg-transparent h-6" style={ { zIndex: 3 } } />
                        </>
                    ) }

                    {/* PERF FIX: Use stable style HTML reference instead of recreating on every render */}
                    <style dangerouslySetInnerHTML={ SLIDER_STYLE_HTML } />
                </div>

                <span className="w-14 whitespace-nowrap">{ formatMinutes( maxMinutes ) }</span>
            </div>
        </div>
    )
})
